import apiClient from "@/api/apiClient";
import { extractApiData } from "@/utils/apiResponseHandler";
import type { DataObject, SimulationRequest, SimulationResult, ReportData } from "@/types";

class AnalyseAPI {
  private readonly model: string;
  constructor() {
    this.model = "analyse";
  }

  /**
   * Upload a sheet file for analysis.
   * @param sheetFile The file to upload.
   * @returns Promise resolving to the analysis result.
   */
  async uploadSheetFile(sheetFile: File): Promise<DataObject[]> {
    const formData = new FormData();
    formData.append("file", sheetFile);

    return await apiClient
      .post(`${this.model}/upload-sheet`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(extractApiData);
  }

  /**
   * Run simulation with provided data.
   * @param data Simulation data containing binsCount and risks.
   * @returns Promise resolving to the simulation result.
   */
  async runSimulation(data: SimulationRequest): Promise<SimulationResult> {
    return await apiClient
      .post(`${this.model}/run-simulation`, data)
      .then(extractApiData);
  }

  /**
   * Generate a report based on analysis data.
   * Initiates download of a .docx report.
   * @param data Data to include in the report.
   * @returns Promise resolving to void.
   */
  async generateReport(data: ReportData): Promise<void> {
    return await apiClient
      .post(`${this.model}/generate-report`, data, {
        responseType: "blob", // Important for binary file handling
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "rapport.docx"); // File name
        document.body.appendChild(link);
        link.click();
        link.remove(); // Clean up the link after download
        window.URL.revokeObjectURL(url); // Clean up the blob URL
      });
  }
}

export default new AnalyseAPI();
